import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * Onboarding - the contract layer for **B46**, `BE-46.1`.
 *
 * `FR-EXP-033` asks for a first board for a new user, but the backend endpoint
 * is deliberately narrower than a board read. `POST /me/onboarding/welcome-board`
 * is an authenticated provisioning action: it either creates the welcome board,
 * finds the existing one, or explains why no board should be opened.
 *
 * The response does not reuse or widen `boardSchema`. The client can open the
 * board through `GET /boards/:id/contents` when this route returns a board id.
 */

/**
 * The provisioning body is intentionally empty and strict. The caller cannot
 * choose the workspace, seed content, locale, template or suppression policy;
 * those are backend decisions tied to `FR-EXP-033`.
 */
export const welcomeBoardProvisionBodySchema = z.strictObject({});

/**
 * The four outcomes `POST /me/onboarding/welcome-board` can report.
 */
export const WELCOME_BOARD_PROVISION_STATUSES = [
  'created',
  'existing',
  'suppressed',
  'not_needed',
] as const;

export const welcomeBoardProvisionStatusSchema = z.enum(WELCOME_BOARD_PROVISION_STATUSES);

/**
 * Reasons are only present for outcomes that do not return a board.
 */
export const WELCOME_BOARD_PROVISION_REASONS = [
  'deleted_welcome_board',
  'existing_workspace_membership',
] as const;

export const welcomeBoardProvisionReasonSchema = z.enum(WELCOME_BOARD_PROVISION_REASONS);

export const welcomeBoardCreatedResultSchema = z.strictObject({
  status: z.literal('created'),
  boardId: ulidSchema,
  workspaceId: ulidSchema,
});

export const welcomeBoardExistingResultSchema = z.strictObject({
  status: z.literal('existing'),
  boardId: ulidSchema,
  workspaceId: ulidSchema,
});

export const welcomeBoardSuppressedResultSchema = z.strictObject({
  status: z.literal('suppressed'),
  reason: z.literal('deleted_welcome_board'),
});

export const welcomeBoardNotNeededResultSchema = z.strictObject({
  status: z.literal('not_needed'),
  reason: z.literal('existing_workspace_membership'),
});

export const welcomeBoardProvisionResultSchema = z.discriminatedUnion('status', [
  welcomeBoardCreatedResultSchema,
  welcomeBoardExistingResultSchema,
  welcomeBoardSuppressedResultSchema,
  welcomeBoardNotNeededResultSchema,
]);

export type WelcomeBoardProvisionBody = z.infer<typeof welcomeBoardProvisionBodySchema>;
export type WelcomeBoardProvisionStatus = z.infer<
  typeof welcomeBoardProvisionStatusSchema
>;
export type WelcomeBoardProvisionReason = z.infer<
  typeof welcomeBoardProvisionReasonSchema
>;
export type WelcomeBoardProvisionResult = z.infer<
  typeof welcomeBoardProvisionResultSchema
>;

/**
 * ── The first-run tip — `FR-EXP-033` AC2 ───────────────────────────────────
 *
 * *"Given the welcome board, when the user drags the first card to the next
 * list, then a confirmation of the interaction is shown and **the tip does not
 * repeat**."*
 *
 * **One-time means once per account, ever** — the acceptance is that it never
 * returns, not that it is dismissible. That is exactly the shape of
 * `user_preferences.has_seen_welcome_tip`: a single user-scoped boolean, no
 * board, no timestamp, no count.
 *
 * ⚠ **`welcomeBoardId` is here, and NOT on `boardSchema`.** The frontend needs
 * to know whether the board in front of it is the caller's welcome board, and
 * `boards.is_welcome_board` has never reached the wire. The obvious move —
 * adding the flag to `boardSchema` — is the wrong one twice over: widening a
 * shipped schema in place is forbidden because one schema serialises several
 * routes (`boardSchema` is referenced 31 times across 12 server files), and it
 * would make **every** board response carry a field that answers a
 * once-per-account question.
 *
 * So the question is asked member-first instead of board-first — *"which board
 * is MY welcome board?"* — and answered with one nullable id the client
 * compares against the board it is rendering. `boards.is_welcome_board` living
 * on `boards` rather than on a join table is what makes a single id sufficient:
 * a user has at most one.
 *
 * `null` means "no live welcome board", which covers both the member who never
 * had one and the member who deleted theirs. That is the same permanence
 * `FR-EXP-033`'s edge case requires of provisioning ("Deleting the welcome
 * board suppresses recreation permanently"), and the same answer
 * `POST /me/onboarding/welcome-board` already gives them as
 * `status: 'suppressed'`.
 */
export const onboardingTipsSchema = z.object({
  welcomeTipSeen: z.boolean(),
  welcomeBoardId: ulidSchema.nullable(),
});

export type OnboardingTips = z.infer<typeof onboardingTipsSchema>;

/**
 * `POST /me/onboarding/tips/welcome-tip/seen` — the body is empty and strict.
 *
 * ⚠ **A `POST` with no payload, deliberately, where a field-scoped `PATCH`
 * would be the house reflex.** Two shapes were considered:
 *
 *  - `PATCH …/welcome-tip` with `{ seen: z.literal(true) }`. It encodes the
 *    one-way-ness in the type, which reads elegantly — and is wrong. A
 *    field-scoped patch whose body has exactly one legal value is not a patch;
 *    it is an acknowledgement carrying a ceremonial payload, and it turns
 *    `{ seen: false }` into a 400 that the next reader diagnoses as a bug
 *    rather than as a policy.
 *  - `PATCH …/welcome-tip` with `{ seen: z.boolean() }`, honouring both
 *    directions. Honest, but it puts an un-see verb in the contract, and
 *    "the tip does not repeat" is strongest when nothing can express its
 *    return.
 *
 * The one-way-ness therefore lives in the handler — an unconditional
 * `has_seen_welcome_tip = true` — and the route is a `POST` with the **same
 * empty-strict-body idiom `welcomeBoardProvisionBodySchema` already uses on
 * the sibling route in this same family**. Idempotent, one-way by
 * construction, and the caller chooses nothing.
 */
export const welcomeTipSeenBodySchema = z.strictObject({});

export type WelcomeTipSeenBody = z.infer<typeof welcomeTipSeenBodySchema>;
